import React from 'react';
import Box from '@mui/material/Box';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import { brand, fonts, CALENDLY_URL, CONTACT_EMAIL } from '../theme/brand';

const symptoms = [
  'Passwords and API keys exposed in the code',
  'Bloated code nobody, including the AI, can safely change',
  'Hosting bills that climb even when traffic doesn’t',
  'Every fix breaks two other things',
  'No tests, no backups, no plan for growth',
  'Logins and payments that aren’t actually safe',
];

const services = [
  {
    num: '01 / Build',
    title: 'New apps, from idea to launch',
    body: 'Customer portals, booking systems, internal tools and AI-powered features. Planned with you, built properly and launched without drama.',
    items: ['Web apps and dashboards', 'iPhone and Android apps', 'AI features and automation', 'Payments, logins and accounts'],
  },
  {
    num: '02 / Secure & host',
    title: 'Built to be safe and to scale',
    body: 'Security comes from years of hands-on cyber security work, not a checklist. It runs in the cloud or on your own hardware, whichever fits your budget.',
    items: ['Security built in from day one', 'Cloud or self-hosted', 'Repeatable, one-click deploys', 'Right-sized hosting costs'],
  },
  {
    num: '03 / Rescue',
    title: 'Already built something?',
    body: 'If a DIY or AI-built app is slow, fragile or worrying you, I’ll slim it down, lock it down and make it something you can grow on.',
    items: ['Cut bloat and untangle code', 'Close security gaps', 'Add tests and backups', 'Speed up slow pages'],
  },
];

const mobile = [
  { title: 'iPhone & iPad', body: 'Native apps built for the App Store.' },
  { title: 'Android', body: 'Apps for Google Play, built to match.' },
  { title: 'Web to app', body: 'Bring an existing web app to mobile.' },
  { title: 'Store approval', body: 'I handle the review process for you.' },
];

const work = [
  {
    client: 'Quote This Job',
    years: '2025–present',
    role: 'Founder & builder',
    title: 'AI estimating for contractors',
    body: 'An AI system that drafts contractor estimates from past quotes and market data. It reads PDFs, spreadsheets and scanned documents and turns them all into one clean format.',
    stack: 'Multi-model AI · AWS · Infrastructure as code',
  },
  {
    client: 'Upbeat Ventures',
    years: '2022–2025',
    role: 'Fractional CTO',
    title: 'AI market analysis platform',
    body: 'Prediction models for market analysis, an automated data-collection pipeline running in the cloud and a web portal where the team explores the results.',
    stack: 'Transformer models · AWS · React',
  },
  {
    client: 'Lavaseats',
    years: '2018–2020',
    role: 'CTO',
    title: 'Product and team, built from zero',
    body: 'Led a team of five engineers to ship a production website and the analytics behind it.',
    stack: 'Python · JavaScript · AWS',
  },
];

const steps = [
  { num: '01', title: 'Free 15-minute call', body: 'Tell me what you want built, or what you’ve built that isn’t working. No prep, no slides, no code required.', meta: 'Free · no commitment', active: true },
  { num: '02', title: 'Plan & fixed quote', body: 'If it’s a fit, you get a clear plan in plain English with a fixed price. No hourly surprises.', meta: 'Fixed scope · fixed price' },
  { num: '03', title: 'Build, launch, hand off', body: 'I build it, launch it and walk you through it. You own the code, the accounts and everything else.', meta: 'Your code · your accounts' },
];

const creds = [
  { big: '15+ years', small: 'Professional engineering since 2010' },
  { big: 'AI practitioner', small: 'Agents, LLMs, fine-tuning, RAG' },
  { big: 'Cyber security', small: 'Years of hands-on security work' },
  { big: 'US patent', small: 'Multi-agent autonomy (No. 9,436,187)' },
];

const chips = ['AI agents & automation', 'Web apps', 'iOS & Android', 'Cloud or self-hosted', 'AWS certified', 'Security hardening', 'Python, React, Rust'];

const container = { maxWidth: 1240, mx: 'auto', px: 3 };

const eyebrow = (color = brand.accent) => ({
  fontFamily: fonts.mono,
  fontSize: 14,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color,
});

const h2 = {
  m: 0,
  fontFamily: fonts.display,
  fontWeight: 900,
  fontSize: 'clamp(34px, 4.2vw, 52px)',
  lineHeight: 1.04,
  letterSpacing: '-0.025em',
};

const card = {
  bgcolor: brand.panel,
  border: `1px solid ${brand.line}`,
  borderRadius: '14px',
};

const button = {
  textDecoration: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.25,
  transition: 'filter 0.15s ease, background-color 0.15s ease',
  '&:hover': { filter: 'brightness(1.08)' },
  '&:focus-visible': { outline: `3px solid ${brand.textStrong}`, outlineOffset: '2px' },
};

const bookLinkProps = { component: 'a', href: CALENDLY_URL, target: '_blank', rel: 'noopener noreferrer' };

const Home = () => {
  return (
    <Box sx={{ bgcolor: brand.ink, color: brand.text, fontFamily: fonts.body, fontSize: 17, lineHeight: 1.55, '& section': { scrollMarginTop: '72px' } }}>

      {/* Hero */}
      <Box component="section" id="top" sx={{ ...container, pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 11 }, display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
        <Box sx={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box sx={eyebrow()}>Custom software · Web · iPhone · Android</Box>
          <Box component="h1" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 900, fontSize: 'clamp(44px, 6.4vw, 84px)', lineHeight: 0.98, letterSpacing: '-0.03em', color: brand.textStrong }}>
            Got an app idea?<br />Build it right the first time.
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: 20, color: brand.textBody, maxWidth: 620 }}>
            AI can produce a demo over a weekend. Making it secure, fast and ready for paying customers is the hard part, and that's the part I do. Tell me what you want built and I'll tell you the smartest way to get there.
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, alignItems: 'center' }}>
            <Box {...bookLinkProps} sx={{ ...button, bgcolor: brand.accent, color: brand.ink, fontSize: 17, px: 3.25, py: 2, minHeight: 52 }}>
              Book a free 15-minute call
              <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box component="a" href="#build" sx={{ ...button, color: brand.text, fontWeight: 500, fontSize: 17, px: 2.75, py: 2, minHeight: 52, border: `1px solid ${brand.lineStrong}`, '&:hover': { bgcolor: brand.panel } }}>
              See what I build
            </Box>
          </Box>
          <Box sx={{ fontSize: 14, color: brand.textMuted }}>No prep, no code and no commitment needed. Just bring the idea.</Box>
        </Box>

        <Box sx={{ ...card, flex: '1 1 380px', minWidth: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.75, borderBottom: `1px solid ${brand.line}`, fontFamily: fonts.mono, fontSize: 13, color: brand.textMuted }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: brand.lineStrong }} />
            ))}
            <Box component="span" sx={{ ml: 1 }}>diy-app --three-months-later</Box>
          </Box>
          <Box sx={{ p: '24px 22px 26px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box component="h2" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 800, fontSize: 22, color: brand.textStrong }}>What DIY AI apps usually ship with</Box>
            {symptoms.map((s) => (
              <Box key={s} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 16, color: brand.textSoft }}>
                <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: brand.warn, flex: 'none', mt: '2px' }} />
                <span>{s}</span>
              </Box>
            ))}
            <Box sx={{ mt: 0.75, pt: 2, borderTop: `1px dashed ${brand.lineStrong}`, fontFamily: fonts.mono, fontSize: 14, color: brand.accent }}>
              → Mine ship without them. (Already have these? I fix those too.)
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Honesty band */}
      <Box component="section" sx={{ bgcolor: brand.accent, color: brand.ink }}>
        <Box sx={{ ...container, py: 5, display: 'flex', flexWrap: 'wrap', gap: '24px 56px', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: '1 1 420px', fontFamily: fonts.display, fontWeight: 900, fontSize: 'clamp(26px, 3vw, 36px)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            I use AI to build, too. The difference is knowing what to keep.
          </Box>
          <Box component="p" sx={{ flex: '1 1 420px', m: 0, fontSize: 17, lineHeight: 1.6, color: brand.accentInk }}>
            AI writes code fast. It doesn't know your security risks, your hosting bill or what happens when ten times the users show up. You get the speed of AI with an engineer making the calls that matter.
          </Box>
        </Box>
      </Box>

      {/* What I build */}
      <Box component="section" id="build" sx={{ ...container, pt: { xs: 9, md: 13 }, pb: { xs: 8, md: 12 }, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 760 }}>
          <Box sx={eyebrow()}>What I build</Box>
          <Box component="h2" sx={{ ...h2, color: brand.textStrong }}>From a sketch on a napkin to software your customers actually use.</Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2.5 }}>
          {services.map((f) => (
            <Box component="article" key={f.num} sx={{ ...card, p: '30px 28px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ fontFamily: fonts.mono, fontSize: 14, color: brand.textMuted }}>{f.num}</Box>
              <Box component="h3" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 800, fontSize: 26, lineHeight: 1.15, color: brand.textStrong }}>{f.title}</Box>
              <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 16 }}>{f.body}</Box>
              <Box component="ul" sx={{ m: '4px 0 0', p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25, fontSize: 15, color: brand.textSoft }}>
                {f.items.map((it) => (
                  <Box component="li" key={it} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: brand.accent, flex: 'none', mt: '3px' }} />
                    <span>{it}</span>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mobile */}
      <Box component="section" id="mobile" sx={{ bgcolor: brand.light, color: brand.lightText }}>
        <Box sx={{ ...container, py: { xs: 9, md: 13 }, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Box sx={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
            <Box sx={eyebrow('#5B4A00')}>Mobile apps</Box>
            <Box component="h2" sx={h2}>Your business, in your customers' pockets.</Box>
            <Box component="p" sx={{ m: 0, fontSize: 19, color: brand.lightBody, maxWidth: 560 }}>
              Native iPhone and Android apps, built together with the backend that powers them. I handle everything through App Store and Google Play approval, so you're not stuck coordinating three vendors.
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.75, mt: 0.75 }}>
              {mobile.map((m) => (
                <Box key={m.title} sx={{ bgcolor: '#FFFFFF', border: `1px solid ${brand.lightLine}`, borderRadius: '10px', p: 2.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ fontWeight: 600, fontSize: 16 }}>{m.title}</Box>
                  <Box sx={{ fontSize: 15, color: '#4A4F5A' }}>{m.body}</Box>
                </Box>
              ))}
            </Box>
            <Box>
              <Box {...bookLinkProps} sx={{ ...button, bgcolor: brand.ink, color: '#FFFFFF', px: 3, py: 1.875, minHeight: 52, mt: 1, '&:focus-visible': { outline: `3px solid ${brand.ink}`, outlineOffset: '2px' } }}>
                Tell me about your app idea
              </Box>
            </Box>
          </Box>

          <Box aria-hidden="true" sx={{ flex: '1 1 300px', display: 'flex', justifyContent: 'center', gap: 2.5, alignItems: 'flex-end' }}>
            <Box sx={{ width: 236, height: 480, bgcolor: brand.ink, borderRadius: '40px', p: 1.5, boxSizing: 'border-box', boxShadow: '0 30px 60px -20px rgba(17,19,24,0.35)' }}>
              <Box sx={{ width: '100%', height: '100%', bgcolor: brand.panel, borderRadius: '30px', display: 'flex', flexDirection: 'column', p: '34px 16px 16px', boxSizing: 'border-box', gap: 1.5 }}>
                <Box sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 18, color: '#FFFFFF' }}>Your App</Box>
                <Box sx={{ height: 96, borderRadius: '12px', bgcolor: brand.accent, display: 'flex', alignItems: 'flex-end', p: 1.5, boxSizing: 'border-box', color: brand.ink, fontWeight: 600, fontSize: 13 }}>Today's bookings</Box>
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ height: 44, borderRadius: '10px', bgcolor: '#262A33' }} />
                ))}
                <Box sx={{ mt: 'auto', height: 44, borderRadius: '10px', border: `1px solid ${brand.lineStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: brand.textSoft, fontSize: 13 }}>Book now</Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 2.5 }}>
              {['iOS', 'Android'].map((p) => (
                <Box key={p} sx={{ bgcolor: brand.ink, color: '#FFFFFF', borderRadius: '10px', px: 1.75, py: 1.25, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIphoneRoundedIcon sx={{ fontSize: 16 }} />{p}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Past work */}
      <Box component="section" id="work" sx={{ ...container, pt: { xs: 9, md: 13 }, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px 48px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 720 }}>
            <Box sx={eyebrow()}>Past work</Box>
            <Box component="h2" sx={{ ...h2, color: brand.textStrong }}>Built for businesses, shipped to production.</Box>
          </Box>
          <Box component="p" sx={{ m: 0, color: brand.textMuted, fontSize: 16, maxWidth: 380 }}>
            A few recent projects, from AI tools for contractors to a whole engineering team built from scratch.
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 2.5 }}>
          {work.map((w) => (
            <Box component="article" key={w.client} sx={{ ...card, p: 3.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, fontFamily: fonts.mono, fontSize: 13, color: brand.textMuted }}>
                <span>{w.client}</span>
                <span>{w.years}</span>
              </Box>
              <Box sx={{ alignSelf: 'flex-start', fontSize: 13, fontWeight: 600, color: brand.ink, bgcolor: brand.accent, borderRadius: '999px', px: 1.5, py: 0.5 }}>{w.role}</Box>
              <Box component="h3" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 800, fontSize: 24, lineHeight: 1.2, color: brand.textStrong }}>{w.title}</Box>
              <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 16 }}>{w.body}</Box>
              <Box sx={{ mt: 'auto', pt: 1.75, borderTop: `1px solid ${brand.line}`, fontSize: 14, color: brand.textSoft }}>{w.stack}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* How it works */}
      <Box component="section" id="process" sx={{ ...container, pt: { xs: 9, md: 13 }, pb: { xs: 8, md: 12 }, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 720 }}>
          <Box sx={eyebrow()}>How it works</Box>
          <Box component="h2" sx={{ ...h2, color: brand.textStrong }}>It starts with a conversation, not a contract.</Box>
        </Box>
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', borderTop: `1px solid ${brand.line}` }}>
          {steps.map((st) => (
            <Box component="li" key={st.num} sx={{ p: '32px 28px 8px 0', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box aria-hidden="true" sx={{ fontFamily: fonts.display, fontWeight: 900, fontSize: 56, lineHeight: 1, color: st.active ? brand.accent : brand.lineStrong }}>{st.num}</Box>
              <Box component="h3" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 800, fontSize: 24, color: brand.textStrong }}>{st.title}</Box>
              <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 16, maxWidth: 360 }}>{st.body}</Box>
              <Box sx={{ fontFamily: fonts.mono, fontSize: 13, color: brand.textMuted }}>{st.meta}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* About */}
      <Box component="section" id="about" sx={{ borderTop: '1px solid #262A33' }}>
        <Box sx={{ ...container, py: { xs: 8, md: 12 }, display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box component="img" src="/dale-arbogast.jpg" alt="Dale Arbogast" sx={{ width: 220, height: 260, objectFit: 'cover', objectPosition: 'center 20%', borderRadius: '14px', border: `1px solid ${brand.line}`, display: 'block' }} />
            <div>
              <Box sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 22, color: brand.textStrong }}>Dale Arbogast</Box>
              <Box sx={{ fontSize: 15, color: brand.textMuted }}>Founder &amp; principal engineer</Box>
            </div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: 14, color: brand.textMuted }}>
              <div>MS Computer Science (Machine Learning), Georgia Tech</div>
              <div>BS Electrical Engineering, Virginia Tech</div>
              <div>BS Mechanical Engineering, Virginia Tech</div>
            </Box>
          </Box>
          <Box sx={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
            <Box component="h2" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 900, fontSize: 'clamp(30px, 3.4vw, 42px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: brand.textStrong }}>
              You talk to the engineer who does the work.
            </Box>
            <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 18 }}>
              I've been a professional engineer since 2010, and I've been building AI for most of that time, from autonomous drone swarms to today's AI agents and language models. I've also spent years working in cyber security, so what I build is secure from the start instead of patched afterward.
            </Box>
            <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 18 }}>
              I've been the technical lead behind several businesses, taking products from idea to production with small teams and real budgets.
            </Box>
            <Box component="p" sx={{ m: 0, color: brand.textBody, fontSize: 18 }}>
              No account managers or offshore handoffs. You get one person who understands both your business and the technology, and who tells you plainly what's worth building.
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5, mt: 0.5 }}>
              {creds.map((c) => (
                <Box key={c.big} sx={{ border: `1px solid ${brand.line}`, borderRadius: '10px', p: '16px 18px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 20, color: brand.textStrong }}>{c.big}</Box>
                  <Box sx={{ fontSize: 14, color: brand.textMuted }}>{c.small}</Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              {chips.map((c) => (
                <Box component="span" key={c} sx={{ fontSize: 14, color: brand.textSoft, border: `1px solid ${brand.lineStrong}`, borderRadius: '999px', px: 1.75, py: 0.875 }}>{c}</Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Final CTA */}
      <Box component="section" id="book" sx={{ px: 3, pb: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1240, mx: 'auto', bgcolor: brand.accent, color: brand.ink, borderRadius: '20px', p: 'clamp(32px, 6vw, 80px)', boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Box sx={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Box sx={eyebrow('#3A3000')}>Free · 15 minutes · No commitment</Box>
            <Box component="h2" sx={{ m: 0, fontFamily: fonts.display, fontWeight: 900, fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              Tell me what you want built.
            </Box>
            <Box component="p" sx={{ m: 0, fontSize: 18, color: brand.accentInk, maxWidth: 620 }}>
              A quick call to talk through your idea. You'll leave knowing what it would take, roughly what it would cost and whether it's worth building at all. Already built something that isn't holding up? Bring that too.
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch' }}>
            <Box {...bookLinkProps} sx={{ ...button, bgcolor: brand.ink, color: '#FFFFFF', fontSize: 18, px: 3.25, py: 2.25, minHeight: 56, borderRadius: '10px', '&:focus-visible': { outline: `3px solid ${brand.ink}`, outlineOffset: '3px' } }}>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
              Book your free call
            </Box>
            <Box component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{ ...button, color: brand.ink, fontSize: 16, px: 2.5, py: 1.75, minHeight: 52, borderRadius: '10px', border: `2px solid ${brand.ink}`, '&:hover': { bgcolor: 'rgba(17,19,24,0.08)' }, '&:focus-visible': { outline: `3px solid ${brand.ink}`, outlineOffset: '3px' } }}>
              Prefer email? Write to me
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
